/* A router in forty lines, instead of a dependency.
 *
 * All this application needs is: real URLs that survive a reload and a paste
 * into Slack, back/forward, and a hook that re-renders on change. Caddy's
 * `try_files {path} /index.html` makes deep links work server-side.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react'

const RouteCtx = createContext<string>('/')

/** One canonical spelling per page.
 *
 *  `match` splits on '/' and compares segment counts, and every route here is
 *  written without a trailing slash -- so `/sites/` matched nothing at all and
 *  the page that worked yesterday answered "No such page" today. Nobody types
 *  that slash on purpose; a URL bar completing from history adds it, and a link
 *  pasted out of one carries it. It is a stray character, not a different page,
 *  so it is removed once here rather than guarded against in twenty branches.
 */
function clean(p: string): string {
  const out = p.replace(/\/+$/, '')
  return out || '/'
}

export function Router({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => clean(window.location.pathname))

  // And in the address bar too, not only in what this app matches on. The URL
  // is the thing people bookmark and paste to each other, so the copy that
  // leaves the browser should be the spelling that works.
  useEffect(() => {
    const c = clean(window.location.pathname)
    if (c !== window.location.pathname) {
      // Query and hash carried through: a password-reset link is `/auth/reset`
      // plus the token in the fragment, and dropping it would break the one
      // journey that cannot be retried.
      window.history.replaceState({}, '', c + window.location.search + window.location.hash)
    }
  }, [])

  useEffect(() => {
    const onPop = () => setPath(clean(window.location.pathname))
    window.addEventListener('popstate', onPop)
    // navigate() dispatches this so a programmatic push re-renders too --
    // pushState alone fires no event.
    window.addEventListener('phoniq:navigate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener('phoniq:navigate', onPop)
    }
  }, [])

  return <RouteCtx.Provider value={path}>{children}</RouteCtx.Provider>
}

export function usePath(): string {
  return useContext(RouteCtx)
}

export function navigate(to: string, replace = false) {
  const next = clean(to)
  if (next === clean(window.location.pathname)) return
  window.history[replace ? 'replaceState' : 'pushState']({}, '', next)
  window.dispatchEvent(new Event('phoniq:navigate'))
  window.scrollTo(0, 0)
}

/** Send a visitor to a page that is theirs, instead of showing them one that
 *  is not.
 *
 *  `replace`, always: Back has to leave this app, not walk into the address we
 *  just decided was the wrong one and bounce forward again. */
export function Redirect({ to }: { to: string }) {
  useEffect(() => {
    navigate(to, true)
  }, [to])
  return null
}

export function useNavigate() {
  return useCallback(navigate, [])
}

export function Link({
  to, children, className, ...rest
}: { to: string; children: ReactNode; className?: string } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>, 'href'
>) {
  const { onClick: theirs, ...anchor } = rest
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // A caller's handler is composed with ours, not swapped for it.
    //
    // `{...rest}` used to be spread after `onClick`, so passing one to a Link
    // silently replaced the navigation. The anchor then did a full page load,
    // which looks like it still works -- and costs a reload, discards whatever
    // the handler had just set up in memory, and is the slowest possible
    // version of an internal link.
    theirs?.(e)
    // Let the browser handle anything that is not a plain left click: cmd-click
    // to open in a new tab has to keep working, or the app feels like a kiosk.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return
    e.preventDefault()
    navigate(to)
  }
  return (
    <a href={to} className={className} {...anchor} onClick={onClick}>
      {children}
    </a>
  )
}

/** Matches '/missions/:id' style patterns. Returns null or the captured parts. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean)
  const a = path.split('/').filter(Boolean)
  if (p.length !== a.length) return null
  const out: Record<string, string> = {}
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!
    const val = a[i]!
    if (seg.startsWith(':')) out[seg.slice(1)] = decodeURIComponent(val)
    else if (seg !== val) return null
  }
  return out
}
